import { Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import { FieldHeader } from './common/FieldHeader';
import { RadioDiffModal } from './Radio/RadioDiffModal';
import { RadioDisplay } from './Radio/RadioDisplay';
import { RadioEmptyState } from './Radio/RadioEmptyState';
import RadioUpdateModal from './Radio/RadioUpdateModal';
import {
  FormFieldPublishingModel,
  FormFieldRadio
} from '@publishing-engine/domain/types/article';
import { FieldType } from '@publishing-engine/domain/types';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { useResetAuthorCopyright } from '../../../api/commands';
import { useToast } from '@morressier/alembic';

/**
 * PublishingModel is ALMOST an Options-Field, but not quite.
 * Its value is { model: x|y, acknowledgeCharge: boolean }.
 * For editing and display purposes, we only care about the 'model' value.
 * So we treat this file as a Façade, pluck out the 'model' value,
 * and defer the rest to Generic Radio Field.
 */

type IProps = {
  formFieldDefinition: FormFieldPublishingModel;
  value: FieldType<FormFieldPublishingModel>;
  previousValue?: FieldType<FormFieldPublishingModel>;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: { model: string; acknowledgeCharge?: boolean }) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?:boolean
};

export const PublishingModelField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const toast = useToast();
  const {
    submissionId,
    submissionContextId,
    organizationId,
    refetchSubmission,
    workflowResponse: {
      workflowData: { copyrightForm }
    }
  } = useSubmissionData();
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const modelValue = value?.model;
  const previousModelValue = previousValue?.model;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const isCopyrightSubmitted = !!copyrightForm?.submitted;

  const asRadio: FormFieldRadio = {
    ...formFieldDefinition,
    component: 'Custom:RadioGroup',
    params: {
      ...formFieldDefinition.params,
      validations: {
        ...formFieldDefinition.params.validations,
        kind: 'options',
        options: Object.keys(formFieldDefinition.params.labels.options)
      }
    }
  };

  const resetCopyrightMutation = useResetAuthorCopyright(
    submissionId,
    submissionContextId,
    () => {},
    organizationId
  );

  const onResetCopyright = async () => {
    if (!isCopyrightSubmitted) return;

    const res = await resetCopyrightMutation.mutateAsync();

    if (!res.isSuccess) {
      return toast({
        variant: 'error',
        title: 'Error invalidating author copyright form',
        description: `An error occured: ${res.message}`,
        isClosable: true
      });
    }

    toast({
      variant: 'success',
      title: 'Existing Copyright Form Deleted.'
    });
  };

  const onSubmit = async (value: string) => {
    setIsEditing(false);
    if (value === modelValue) return;

    await Promise.all([
      onResetCopyright(),
      onChange!({
        model: value,
        ...(value === 'openAccess' ? { acknowledgeCharge: true } : {})
      })
    ]);

    refetchSubmission();
  };

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <RadioUpdateModal
          formField={asRadio}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={modelValue!}
          onSubmit={onSubmit}
          showCopyrightWarning={isCopyrightSubmitted}
        />
      )}

      {isUpdated && showDiffModal ? (
        <RadioDiffModal
          fieldName={formFieldDefinition.params.labels.main}
          value={modelValue}
          previousValue={previousModelValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <RadioDisplay
          value={formFieldDefinition.params.labels.options[modelValue!]?.main!}
        />
      ) : (
        <RadioEmptyState />
      )}
    </Flex>
  );
};
